import React, { useState, useEffect } from 'react';
import { B } from '../constants';
import { getTripImageByDestination } from '../lib/tripImages';
import { borderRadius } from '../designTokens';
import { blogs } from '../api';
import Input from '../components/ui/Input';
import BlogRenderer from '../components/BlogRenderer';

const DEST_IMAGES = {
  'hyderabad': 'photo-1599661046289-e31897846e41',
  'rishikesh': 'photo-1544367567-0f2fcb009e0b',
  'bali': 'photo-1537996194471-e657df975ab4',
  'tokyo': 'photo-1540959733332-eab4deabeeaf',
  'santorini': 'photo-1613395877344-13d4a8e0d49e',
  'machu picchu': 'photo-1587595431973-160d0d94add1',
  'paris': 'photo-1502602898657-3e91760cbb34',
  'safari': 'photo-1516426122078-c23e76319801',
  'maldives': 'photo-1514282401047-d79a71a590e8',
  'swiss alps': 'photo-1531366936337-7c912a4589a7',
  'bangkok': 'photo-1508009603885-50cf7c579365',
  'ibiza': 'photo-1539037116277-4db20889f2d4',
  'dubai': 'photo-1512453979798-5ea266f8880c',
  'utah': 'photo-1506905925346-21bda4d32df4',
  'zion': 'photo-1506905925346-21bda4d32df4',
  'india': 'photo-1544367567-0f2fcb009e0b',
  'indonesia': 'photo-1537996194471-e657df975ab4',
  'japan': 'photo-1540959733332-eab4deabeeaf',
  'greece': 'photo-1613395877344-13d4a8e0d49e',
  'peru': 'photo-1587595431973-160d0d94add1',
  'france': 'photo-1502602898657-3e91760cbb34',
  'kenya': 'photo-1516426122078-c23e76319801',
  'switzerland': 'photo-1531366936337-7c912a4589a7',
  'thailand': 'photo-1508009603885-50cf7c579365',
  'spain': 'photo-1539037116277-4db20889f2d4',
  'uae': 'photo-1512453979798-5ea266f8880c',
};

function blogHero(blogData) {
  if (blogData.trip?.coverImage && /^https?:\/\//i.test(blogData.trip.coverImage)) return blogData.trip.coverImage;
  const dest = (blogData.trip?.destination || '').toLowerCase();
  for (const [key, photoId] of Object.entries(DEST_IMAGES)) {
    if (dest.includes(key)) return `https://images.unsplash.com/${photoId}?w=900&q=80`;
  }
  return 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=900&q=80';
}

export default function BlogViewScreen({ blog, goScreen, showToast }) {
  const [blogData, setBlogData] = useState(blog);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(blog?.title || '');
  const [editContent, setEditContent] = useState(blog?.content || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (blog?.tripId) {
      blogs.get(blog.tripId).then(setBlogData).catch(console.error);
    }
  }, [blog?.tripId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await blogs.update(blogData.tripId, { title: editTitle, content: editContent });
      setBlogData(updated);
      setEditing(false);
      showToast('Blog saved!');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      const updated = await blogs.update(blogData.tripId, { isPublished: !blogData.isPublished });
      setBlogData(updated);
      showToast(updated.isPublished ? 'Published!' : 'Unpublished');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  if (!blogData) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 20px' }}>
      <button onClick={() => goScreen('blogs')} aria-label="Back to Blogs" style={{ background: 'none', border: 'none', color: B.muted, cursor: 'pointer', marginBottom: 16, fontSize: 14 }}>
        ← Back to Blogs
      </button>

      {editing ? (
        <div>
          <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} style={{
            width: '100%', fontFamily: "'Playfair Display',serif", fontSize: 28, border: 'none', borderBottom: '2px solid #ddd',
            padding: '8px 0', marginBottom: 16, outline: 'none',
          }} />
          <textarea value={editContent} onChange={e => setEditContent(e.target.value)} style={{
            width: '100%', minHeight: 400, border: '1px solid #ddd', borderRadius: 12, padding: 16,
            fontSize: 16, lineHeight: 1.8, fontFamily: "'Montserrat',sans-serif", resize: 'vertical',
          }} />
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button onClick={handleSave} disabled={saving} style={{ background: B.forest, color: 'white', border: 'none', padding: '12px 24px', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} style={{ background: '#ddd', color: B.night, border: 'none', padding: '12px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div style={{
            background: `linear-gradient(rgba(15,25,35,0.45), rgba(15,25,35,0.65)), url(${blogHero(blogData)}) center/cover`,
            borderRadius: borderRadius.lg, padding: '40px 32px', marginBottom: 24, minHeight: 220,
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          }}>
            <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 36, fontWeight: 800, color: '#fff', marginBottom: 8 }}>{blogData.title}</h1>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 15 }}>
              {blogData.trip?.destination} • {blogData.updatedAt ? new Date(blogData.updatedAt).toLocaleDateString() : ''}
            </p>
          </div>

          <BlogRenderer content={blogData.content || ''} experiences={blogData.trip?.experiences || []} />

          {blogData.trip?.experiences?.length > 0 && (
            <div style={{ marginTop: 32 }}>
              <h4 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 700, fontSize: 15, marginBottom: 16, color: B.night }}>📍 Places Visited</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
                {blogData.trip.experiences.map(exp => (
                  <div key={exp.id} style={{
                    background: B.sand,
                    borderRadius: 14,
                    overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)'; }}
                  >
                    {exp.photos?.[0] && (
                      <img
                    src={exp.photos[0].url}
                    alt={exp.location || exp.title}
                    style={{ width: '100%', height: 140, objectFit: 'cover' }}
                    onError={(e) => {
                      const img = e.currentTarget;
                      if (img.dataset.fallback) return;
                      img.dataset.fallback = '1';
                      img.src = getTripImageByDestination(exp.location || exp.title || '');
                    }}
                  />
                    )}
                    <div style={{ padding: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14, color: B.night }}>{exp.title}</div>
                          {exp.location && <div style={{ fontSize: 12, color: B.muted, marginTop: 2 }}>📍 {exp.location}</div>}
                        </div>
                        <div style={{ fontSize: 12, color: B.amber, whiteSpace: 'nowrap', marginLeft: 8 }}>
                          {[1,2,3,4,5].map(r => (
                            <span key={r} style={{ color: r <= (exp.rating || 0) ? B.amber : '#ddd' }}>★</span>
                          ))}
                        </div>
                      </div>
                      {exp.content && (
                        <p style={{ fontSize: 12, color: '#666', lineHeight: 1.5, marginTop: 6 }}>{exp.content}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, marginTop: 32, paddingTop: 24, borderTop: '1px solid #eee' }}>
            <button onClick={() => setEditing(true)} style={{ background: B.forest, color: 'white', border: 'none', padding: '10px 20px', borderRadius: 20, cursor: 'pointer', fontWeight: 600 }}>
              ✏️ Edit
            </button>
            <button onClick={handlePublish} style={{ background: blogData.isPublished ? '#ddd' : B.forest, color: blogData.isPublished ? B.night : 'white', border: 'none', padding: '10px 20px', borderRadius: 20, cursor: 'pointer', fontWeight: 600 }}>
              {blogData.isPublished ? 'Unpublish' : 'Publish'}
            </button>
            {blogData.trip?.destination && (
              <button onClick={() => goScreen('ai', { prefillDestination: blogData.trip.destination, prefillDays: blogData.trip.days?.length || null })}
                style={{ background: B.amber, color: 'white', border: 'none', padding: '10px 20px', borderRadius: 20, cursor: 'pointer', fontWeight: 600 }}>
                ✈️ Plan this trip
              </button>
            )}
            {blogData.trip?.id && (
              <button onClick={() => goScreen('tripDetail', { id: blogData.trip.id })} style={{ background: '#f0f0f0', color: B.night, border: 'none', padding: '10px 20px', borderRadius: 20, cursor: 'pointer', fontWeight: 600 }}>
                View Trip
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
